// src/pages/LojaDashboard.jsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Chip,
  Stack,
  Divider,
} from "@mui/material";
import AssignmentIcon from "@mui/icons-material/Assignment";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import HandymanRoundedIcon from "@mui/icons-material/HandymanRounded";
import PageLayout from "../components/PageLayout";
import { useAuth } from "../contexts/AuthContext";
import { db } from "../contexts/firebase/config";
import {
  collection,
  query,
  onSnapshot,
  orderBy,
  limit,
} from "firebase/firestore";

export default function LojaDashboard() {
  const { currentUser } = useAuth();

  const [stats, setStats] = useState({
    total: 0,
    emCurso: 0,
    concluidas: 0,
  });
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  const fmtDate = (v) => {
    if (!v) return "—";
    if (v?.seconds) return new Date(v.seconds * 1000).toLocaleDateString();
    try {
      return new Date(v).toLocaleDateString();
    } catch {
      return "—";
    }
  };

  useEffect(() => {
    const reservasRef = collection(db, "reservas");

    const unsubStats = onSnapshot(
      reservasRef,
      (snap) => {
        const total = snap.size;
        let concluidas = 0;

        snap.docs.forEach((d) => {
          const st = (d.data().status || "").toLowerCase();
          if (st === "concluído" || st === "concluido") {
            concluidas++;
          }
        });

        const emCurso = total - concluidas;
        setStats({ total, emCurso, concluidas });
        setLoading(false);
      },
      () => setLoading(false)
    );

    const qRecent = query(reservasRef, orderBy("createdAt", "desc"), limit(5));

    const unsubRecent = onSnapshot(qRecent, (snap) => {
      setRecent(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });

    return () => {
      unsubStats();
      unsubRecent();
    };
  }, []);

  const cards = [
    {
      label: "Total de Reservas",
      value: stats.total,
      icon: <AssignmentIcon sx={{ fontSize: 28, color: "#2563eb" }} />,
      tint: "rgba(37,99,235,0.10)",
    },
    {
      label: "Em Curso",
      value: stats.emCurso,
      icon: <HourglassEmptyIcon sx={{ fontSize: 28, color: "#d97706" }} />,
      tint: "rgba(245,158,11,0.12)",
    },
    {
      label: "Concluídas",
      value: stats.concluidas,
      icon: <CheckCircleIcon sx={{ fontSize: 28, color: "#059669" }} />,
      tint: "rgba(16,185,129,0.10)",
    },
  ];

  const statusChipProps = (status) => {
    if (!status) {
      return {
        label: "—",
        sx: {
          fontWeight: 800,
          borderRadius: "999px",
          bgcolor: "rgba(15,23,42,0.04)",
          border: "1px solid rgba(15,23,42,0.08)",
        },
      };
    }

    const st = status.toLowerCase();

    if (st.includes("conclu")) {
      return {
        label: "Concluído",
        sx: {
          fontWeight: 800,
          borderRadius: "999px",
          bgcolor: "rgba(16,185,129,0.12)",
          color: "#065f46",
          border: "1px solid rgba(16,185,129,0.18)",
        },
      };
    }

    if (st.includes("pronto")) {
      return {
        label: "Pronto p/ levantamento",
        sx: {
          fontWeight: 800,
          borderRadius: "999px",
          bgcolor: "rgba(59,130,246,0.12)",
          color: "#1d4ed8",
          border: "1px solid rgba(59,130,246,0.18)",
        },
      };
    }

    if (st.includes("reparação") || st.includes("reparacao")) {
      return {
        label: "Em reparação",
        sx: {
          fontWeight: 800,
          borderRadius: "999px",
          bgcolor: "rgba(245,158,11,0.12)",
          color: "#92400e",
          border: "1px solid rgba(245,158,11,0.20)",
        },
      };
    }

    return {
      label: status,
      sx: {
        fontWeight: 800,
        borderRadius: "999px",
        bgcolor: "rgba(15,23,42,0.04)",
        border: "1px solid rgba(15,23,42,0.08)",
      },
    };
  };

  return (
    <PageLayout>
      <Box
        sx={{
          minHeight: "100vh",
          width: "100%",
          background:
            "linear-gradient(180deg, #f8fafc 0%, #eef2ff 42%, #ffffff 100%)",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 1280,
            mx: "auto",
            px: { xs: 2, sm: 3, md: 4 },
            py: { xs: 3, md: 4 },
          }}
        >
          <Paper
            elevation={0}
            sx={{
              position: "relative",
              overflow: "hidden",
              mb: 4,
              p: { xs: 3, md: 4.5 },
              borderRadius: "32px",
              border: "1px solid rgba(255,255,255,0.68)",
              background:
                "linear-gradient(135deg, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.72) 100%)",
              backdropFilter: "blur(14px)",
              boxShadow: "0 24px 80px rgba(15, 23, 42, 0.08)",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: -60,
                right: -60,
                width: 220,
                height: 220,
                borderRadius: "50%",
                background: "rgba(99,102,241,0.14)",
                filter: "blur(42px)",
              }}
            />
            <Box
              sx={{
                position: "absolute",
                bottom: -40,
                left: "22%",
                width: 180,
                height: 180,
                borderRadius: "50%",
                background: "rgba(16,185,129,0.10)",
                filter: "blur(42px)",
              }}
            />

            <Grid
              container
              spacing={4}
              alignItems="center"
              sx={{ position: "relative", zIndex: 1 }}
            >
              <Grid item xs={12} lg={7}>
                <Stack spacing={2.25}>
                  <Stack direction="row" spacing={1.2} alignItems="center">
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2.5,
                        display: "grid",
                        placeItems: "center",
                        background:
                          "linear-gradient(135deg, #4f46e5 0%, #2563eb 100%)",
                        color: "common.white",
                        boxShadow: "0 10px 24px rgba(79,70,229,0.24)",
                      }}
                    >
                      <StorefrontRoundedIcon fontSize="small" />
                    </Box>

                    <Chip
                      label="Dashboard da Loja"
                      sx={{
                        width: "fit-content",
                        fontWeight: 800,
                        color: "#4338ca",
                        bgcolor: "rgba(99,102,241,0.08)",
                        borderRadius: "999px",
                        border: "1px solid rgba(99,102,241,0.12)",
                      }}
                    />
                  </Stack>

                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 900,
                        letterSpacing: "-0.05em",
                        fontSize: { xs: "2rem", sm: "2.4rem", md: "3rem" },
                        lineHeight: 1.03,
                        color: "#0f172a",
                      }}
                    >
                      👋 Olá,{" "}
                      {currentUser?.displayName ||
                        currentUser?.email?.split("@")[0]}
                    </Typography>

                    <Typography
                      sx={{
                        mt: 1.5,
                        maxWidth: 720,
                        color: "text.secondary",
                        fontSize: { xs: "0.98rem", md: "1.08rem" },
                        lineHeight: 1.75,
                      }}
                    >
                      Aqui encontras um resumo do estado atual das reservas da
                      loja, com acesso rápido aos indicadores principais e às
                      entradas mais recentes.
                    </Typography>
                  </Box>
                </Stack>
              </Grid>

              <Grid item xs={12} lg={5}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.25,
                    borderRadius: "24px",
                    border: "1px solid rgba(15,23,42,0.06)",
                    background: "rgba(255,255,255,0.74)",
                    boxShadow: "0 12px 30px rgba(15,23,42,0.05)",
                  }}
                >
                  <Stack direction="row" spacing={1.3} alignItems="center">
                    <Box
                      sx={{
                        width: 46,
                        height: 46,
                        borderRadius: "16px",
                        display: "grid",
                        placeItems: "center",
                        bgcolor: "rgba(37,99,235,0.10)",
                        color: "#2563eb",
                      }}
                    >
                      <TrendingUpRoundedIcon />
                    </Box>

                    <Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontWeight: 700 }}
                      >
                        Estado atual
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "1.2rem",
                          fontWeight: 900,
                          letterSpacing: "-0.03em",
                        }}
                      >
                        {loading ? "…" : `${stats.emCurso} reservas em curso`}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          </Paper>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Grid container spacing={3} sx={{ mb: 0.5 }}>
                  {cards.map(({ label, value, icon, tint }) => (
                    <Grid item xs={12} sm={6} md={4} key={label}>
                      <Card
                        elevation={0}
                        sx={{
                          height: "100%",
                          borderRadius: "26px",
                          border: "1px solid rgba(15,23,42,0.06)",
                          background: "rgba(255,255,255,0.82)",
                          boxShadow: "0 16px 38px rgba(15,23,42,0.06)",
                          transition: "all .25s ease",
                          "&:hover": {
                            transform: "translateY(-4px)",
                            boxShadow: "0 22px 48px rgba(15,23,42,0.10)",
                          },
                        }}
                      >
                        <CardContent sx={{ p: 2.75 }}>
                          <Stack
                            direction="row"
                            alignItems="flex-start"
                            justifyContent="space-between"
                            spacing={2}
                          >
                            <Box>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "text.secondary",
                                  fontWeight: 700,
                                }}
                              >
                                {label}
                              </Typography>

                              <Typography
                                sx={{
                                  mt: 1,
                                  fontSize: "2rem",
                                  fontWeight: 900,
                                  lineHeight: 1,
                                  letterSpacing: "-0.04em",
                                  color: "#0f172a",
                                }}
                              >
                                {value}
                              </Typography>
                            </Box>

                            <Box
                              sx={{
                                width: 54,
                                height: 54,
                                borderRadius: "18px",
                                display: "grid",
                                placeItems: "center",
                                bgcolor: tint,
                                border: "1px solid rgba(15,23,42,0.05)",
                              }}
                            >
                              {icon}
                            </Box>
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 2.5, md: 3 },
                    borderRadius: "28px",
                    border: "1px solid rgba(15,23,42,0.06)",
                    background: "rgba(255,255,255,0.78)",
                    backdropFilter: "blur(12px)",
                    boxShadow: "0 24px 60px rgba(15,23,42,0.06)",
                  }}
                >
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    spacing={2}
                    sx={{ mb: 2.5 }}
                  >
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 900 }}>
                        📋 Reservas Recentes
                      </Typography>
                      <Typography color="text.secondary" sx={{ mt: 0.6 }}>
                        Consulta rapidamente as últimas reservas registadas.
                      </Typography>
                    </Box>

                    <Chip
                      icon={<ReceiptLongRoundedIcon />}
                      label={`${recent.length} registo(s)`}
                      sx={{
                        borderRadius: "999px",
                        fontWeight: 800,
                        bgcolor: "rgba(15,23,42,0.04)",
                      }}
                    />
                  </Stack>

                  <TableContainer
                    sx={{
                      borderRadius: "22px",
                      border: "1px solid rgba(15,23,42,0.06)",
                      overflow: "hidden",
                    }}
                  >
                    <Table size="small">
                      <TableHead>
                        <TableRow
                          sx={{
                            "& th": {
                              bgcolor: "rgba(248,250,252,0.92)",
                              fontWeight: 800,
                              color: "text.secondary",
                              borderBottom: "1px solid rgba(15,23,42,0.06)",
                              fontSize: "0.8rem",
                              textTransform: "uppercase",
                              letterSpacing: "0.03em",
                            },
                          }}
                        >
                          <TableCell>Serviço</TableCell>
                          <TableCell>Cliente</TableCell>
                          <TableCell>Entrega</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {recent.length === 0 ? (
                          <TableRow>
                            <TableCell
                              colSpan={4}
                              align="center"
                              sx={{ py: 6, color: "text.secondary" }}
                            >
                              Nenhuma reserva recente.
                            </TableCell>
                          </TableRow>
                        ) : (
                          recent.map((r) => {
                            const chip = statusChipProps(r.status);
                            return (
                              <TableRow
                                key={r.id}
                                hover
                                sx={{
                                  "& td": {
                                    borderBottom:
                                      "1px solid rgba(15,23,42,0.05)",
                                  },
                                  "&:last-child td": { borderBottom: 0 },
                                }}
                              >
                                <TableCell sx={{ fontWeight: 700 }}>
                                  {r.serviceTitle || "(serviço)"}
                                </TableCell>
                                <TableCell>
                                  {r.clienteNome || r.clienteId}
                                </TableCell>
                                <TableCell>{fmtDate(r.dataEntrega)}</TableCell>
                                <TableCell>
                                  <Chip size="small" label={chip.label} sx={chip.sx} />
                                </TableCell>
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Divider sx={{ my: 2.25 }} />

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ lineHeight: 1.7 }}
                  >
                    Usa esta área para acompanhar rapidamente o volume de
                    trabalho da loja e o progresso das reservas mais recentes.
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          )}
        </Box>
      </Box>
    </PageLayout>
  );
}